/**
 * Placeholder bones for a screen that has arrived but whose data has not. Pulse is the only motion,
 * and it is off when the visitor asked for less motion. Bones match the controls they stand in for,
 * so a loading step has the same layout as the loaded one.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/motion.stylex";
import { motion } from "@lattice/tokens/motion.stylex";
import "@lattice/tokens/size.stylex";
import { size } from "@lattice/tokens/size.stylex";
import "@lattice/tokens/space.stylex";
import { border, radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
const pulse = "xreb3et-B";
const styles = {
  bone: {
    k1xSpc: "x1lliihq",
    kWkggS: "x1890um5",
    k3nNDw: "x1nf803f",
    kKVMdj: "xzqjj77 x1ah4f44",
    k44tkh: "xcdydla",
    kyAemX: "x4hg4is",
    ko0y90: "xa4qsjk",
    kILWW9: "xpz12be",
    $$css: true
  },
  line: {
    kZKoxP: "x3ycs4o",
    kzqmXN: "xh8yej3",
    kaIpWk: "x8wwnw1",
    $$css: true
  },
  title: {
    kZKoxP: "x18fbomr",
    kzqmXN: "xh00ny3",
    kaIpWk: "x8wwnw1",
    $$css: true
  },
  control: {
    kZKoxP: "xdd8jsf",
    kzqmXN: "xh8yej3",
    kaIpWk: "x1m3fsnl",
    $$css: true
  },
  row: {
    kZKoxP: "xy75621",
    kzqmXN: "xh8yej3",
    kaIpWk: "x1gzjth6",
    $$css: true
  },
  avatar: {
    kZKoxP: "xctwnvt",
    kzqmXN: "x1g6ucx3",
    kaIpWk: "x1m3fsnl",
    $$css: true
  }
};

/** How long a load must last before its bones show. A faster one never flashes them. */
const SHOW_AFTER_MS = 200;
export type Bone = "line" | "title" | "control" | "row" | "avatar";
export function Skeleton({
  bone = "line",
  testId
}: {
  readonly bone?: Bone;
  readonly testId?: string;
  /** What is loading, for the screen reader: "orders". */
  readonly noun?: string;
}) {
  return <span data-testid={testId} aria-hidden="true" {...stylex.props(styles.bone, bone === "title" ? styles.title : bone === "control" ? styles.control : bone === "row" ? styles.row : bone === "avatar" ? styles.avatar : styles.line)} />;
}

/** A load that lasts this long gets a sentence saying so. */
const SLOW_AFTER_MS = 3000;
interface Stage {
  readonly shown: boolean;
  readonly slow: boolean;
}
function useShown(): Stage {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const first = window.setTimeout(() => setStage(1), SHOW_AFTER_MS);
    const second = window.setTimeout(() => setStage(2), SLOW_AFTER_MS);
    return () => {
      window.clearTimeout(first);
      window.clearTimeout(second);
    };
  }, []);
  return {
    shown: stage >= 1,
    slow: stage >= 2
  };
}

/** The words a screen reader gets for bones, and a sentence for a load that drags on. */
function LoadingNote({
  slow,
  noun
}: {
  readonly slow: boolean;
  readonly noun: string;
}) {
  return <p role="status" {...{
    0: {
      className: "x1ghz6dp x1lm7cez xk0dswh"
    },
    1: {
      className: "x1ghz6dp x1lm7cez xk0dswh x10l6tqk x1i1rx1s xjm9jq1 xb3r6kr x1hyvwdk xuxw1ft"
    }
  }[!!!slow << 0]}>
      {slow ? `Still loading ${noun}. This is taking longer than usual.` : `Loading ${noun}`}
    </p>;
}

/** Bones for the rows of a table, one cell per column. */
export function TableRowsSkeleton({
  columns,
  rows,
  testId = "table-skeleton",
  noun = "the table"
}: {
  readonly columns: number;
  readonly rows: number;
  readonly testId?: string;
  /** What is loading, for the screen reader: "orders". */
  readonly noun?: string;
}) {
  const {
    shown,
    slow
  } = useShown();
  return <div data-testid={testId} {...{
    0: {
      className: "x78zum5 xdt5ytf xeuugli"
    },
    1: {
      className: "x78zum5 xdt5ytf xeuugli xlshs6z"
    }
  }[!!!shown << 0]}>
      <LoadingNote slow={slow} noun={noun} />
      {Array.from({
      length: rows
    }, (_, row) => <div key={row} className="x78zum5 x626il7 x9f619 x1oiv393 x11falgf x1q0g3np x6phar x6s0dn4 x176h1w7 xt970qd x1r5s8cc xerji9v xq3vdme x1q0q8m5 x4a0p42">
          {Array.from({
        length: columns
      }, (_cell, column) => <span key={column} className="x1cqoux5 xeuugli">
              <Skeleton />
            </span>)}
        </div>)}
    </div>;
}

/** Bones for the rows of a list: an avatar, a title and a line. */
export function ListRowsSkeleton({
  rows,
  testId = "list-skeleton",
  noun = "the list"
}: {
  readonly rows: number;
  readonly testId?: string;
  /** What is loading, for the screen reader: "orders". */
  readonly noun?: string;
}) {
  const {
    shown,
    slow
  } = useShown();
  return <div data-testid={testId} {...{
    0: {
      className: "x78zum5 xdt5ytf xeuugli"
    },
    1: {
      className: "x78zum5 xdt5ytf xeuugli xlshs6z"
    }
  }[!!!shown << 0]}>
      <LoadingNote slow={slow} noun={noun} />
      {Array.from({
      length: rows
    }, (_, row) => <div key={row} className="x78zum5 x626il7 x9f619 x1oiv393 x11falgf x1q0g3np x6phar x6s0dn4 x176h1w7 xt970qd x1r5s8cc xerji9v xq3vdme x1q0q8m5 x4a0p42">
          <Skeleton bone="avatar" />
          <span className="x1cqoux5 xeuugli">
            <Skeleton />
          </span>
        </div>)}
    </div>;
}

/** Bones for a description list: a short term over a longer value, `items` times. */
export function DescriptionListSkeleton({
  items,
  testId = "description-skeleton",
  noun = "the details"
}: {
  readonly items: number;
  readonly testId?: string;
  /** What is loading, for the screen reader: "orders". */
  readonly noun?: string;
}) {
  const {
    shown,
    slow
  } = useShown();
  return <div data-testid={testId} {...{
    0: {
      className: "x78zum5 xdt5ytf xeuugli"
    },
    1: {
      className: "x78zum5 xdt5ytf xeuugli xlshs6z"
    }
  }[!!!shown << 0]}>
      <LoadingNote slow={slow} noun={noun} />
      {Array.from({
      length: items
    }, (_, item) => <div key={item} className="x78zum5 xdt5ytf xskm3c9 x1xqhfp">
          <span className="x1lliihq x1890um5 x1nf803f xzqjj77 x1ah4f44 xcdydla x4hg4is xa4qsjk xpz12be x3ycs4o xxljpkc x8wwnw1" />
          <span className="x1lliihq x1890um5 x1nf803f xzqjj77 x1ah4f44 xcdydla x4hg4is xa4qsjk xpz12be x1sqxqiv xycev2y x8wwnw1" />
        </div>)}
    </div>;
}